import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { esEnvioIncierto } from '../convert-usdt-ves/convert-usdt-ves.rules';
import {
  BalanceResponse,
  BinanceWithdrawalService,
} from '../../services/binance-withdrawal.service';
import {
  UsdtDestinationData,
  UsdtDestinationModalComponent,
  UsdtDestinationResult,
} from '../calculator-cryto/components/usdt-destination-modal/usdt-destination-modal.component';
import { MainRatesService } from '../../services/main-rates.service';
import { AddBeneficiaryComponent } from '../calculator-cryto/components/add-beneficiary/add-beneficiary.component';
import { RecipientCriptoService } from '../../services/recipient-cripto.service';
import {
  TramosVesUsdtResponse,
  TramoVesUsdt,
  WalletVesClientService,
} from '../../services/wallet-ves-client.service';
import {
  avisarCambioDeTasa,
  ESTADO_TRAMOS_INICIAL,
  EstadoTramos,
  LecturaTramos,
  registrarLectura,
  TasaTramo,
  tasaParaMontoUsdt,
  tasaParaMontoVes,
  tramosDesactualizados,
  tramosParaCotizar,
  tramosValidos,
} from './convert-ves-usdt.tramos';
import { EscalonVista } from '../home-rates/models/home-rates.model';
import { puedeRecargarVes, tieneSaldoVes } from 'src/app/shared/utils/saldo-ves';
import { escaleraDesde } from '../home-rates/rules/home-rates.rules';
import { MediosCriptoService } from '../../services/medios-cripto.service';
import {
  avisoRedesPausadas,
  beneficiarioPausado,
  conRedPausada,
  esErrorRedInactiva,
  esRedBinance,
  EstadoRed,
  etiquetaCortaRed,
  MEDIO_CRIPTO_TRC20,
  mensajeRedInactiva,
  redActiva,
  redDeBeneficiario,
} from 'src/app/shared/utils/red-cripto';

@Component({
  selector: 'app-convert-ves-usdt',
  templateUrl: './convert-ves-usdt.component.html',
  styleUrls: ['./convert-ves-usdt.component.sass'],
})
export class ConvertVesUsdtComponent implements OnInit, OnDestroy {
  // Step management
  step: 1 | 2 | 3 = 1;
  destination: 'wallet' | 'withdraw' | null = null;

  // Responsive modal
  isVertical = false;
  @HostListener('window:resize')
  onWindowResize() {
    this.isVertical = window.innerWidth <= 768;
  }

  // Beneficiarios (withdraw path)
  beneficiarioSeleccionado: any = null;
  listaBeneficiarios: any[] = [];
  listaBeneficiariosFiltrados: any[] = [];
  searchBeneficiario: string = '';
  loadingBeneficiarios: boolean = false;

  // Redes cripto. El retiro lo hace Nest por Binance, que solo paga por
  // TRC20: la lista trae únicamente beneficiarios de esa red (uno de Polygon
  // o Solana se pagaría en la red equivocada). Los demás se cuentan para
  // explicar por qué no aparecen.
  estadoRedes: EstadoRed[] = [];
  beneficiariosOtrasRedes: number = 0;

  // Configuración
  readonly MIN_USDT_AMOUNT = 20;

  // Usuario
  user: any;
  tipoId: string = '';
  subscription: Subscription = new Subscription();

  // Saldos
  saldoVes: number = 0;
  saldoUsdt: number = 0;

  // Tasa
  // `tasaVesUsdt` es la tasa efectiva: la del tramo del monto actual (o la
  // base si no hay tramos). Es la que se muestra, calcula y envía.
  tasaVesUsdt: number = 0;
  // `tasaFinal` de rates/ves-usdt/rate, la de siempre.
  tasaBase: number = 0;
  // Tramos VES→USDT: lo último bueno y los fallos seguidos. Un fallo suelto
  // conserva los tramos de antes; 3 seguidos (o datos de más de 5 minutos)
  // caen a la base como "tasa estimada".
  estadoTramos: EstadoTramos = ESTADO_TRAMOS_INICIAL;
  tramoActual: TramoVesUsdt | null = null;
  letraTramoActual: string = '';
  ahorroVes: number = 0;
  ahorroUsdt: number = 0;
  promedioBinance: number = 0;
  comisionPct: number = 0;
  loadingRate: boolean = true;
  rateError: string | null = null;

  // Montos
  montoVes: number = 0;
  montoUsdt: number = 0;

  // Horario de operación
  horarioOperacion: string = '4:00 - 23:00';
  isOperatingHours: boolean = true;
  operatingHoursMessage: string =
    'No puedes operar en este momento. El horario de operación es de 4:00 a 23:00 (hora de Venezuela).';

  // Estados de UI
  loading: boolean = false;
  isConverting: boolean = false;
  isTransitioning: boolean = false;
  isModalOpen: boolean = false;
  slideDirection: 'left' | 'right' = 'left';
  errorMessage: string | null = null;
  showRateChangedWarning: boolean = false;
  previousRate: number = 0;

  // Anti-hackeo: bloqueo permanente tras envío exitoso
  operationCompleted: boolean = false;

  /**
   * La compra (o compra + retiro) falló sin respuesta clara: pudo haberse
   * hecho. La pantalla queda cerrada (sin reintento) y se manda al historial.
   */
  envioIncierto: boolean = false;
  static readonly MENSAJE_INCIERTO =
    'No pudimos confirmar tu operación. Revisa tu historial antes de intentar de nuevo.';

  private timeCheckInterval: any;
  private rateRefreshInterval: any;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private spinner: SpinnerService,
    private binanceWithdrawalService: BinanceWithdrawalService,
    private mainRatesService: MainRatesService,
    private recipientCriptoService: RecipientCriptoService,
    private walletVesClientService: WalletVesClientService,
    private mediosCripto: MediosCriptoService,
  ) {
    this.subscription.add(
      this.store.select('auth').subscribe(async ({ user }: any) => {
        this.user = user;
        this.tipoId = user?.tipoId || '';

        // Guard: solo quien tiene saldo VES (familia VE o chileno con VES)
        if (this.tipoId && !tieneSaldoVes(this.tipoId, this.user?.numeroId)) {
          this.router.navigate(['/wallet/home']);
          return;
        }

        if (this.user?.usuarioId) {
          await this.obtenerTasa();
          await this.obtenerSaldoVes();
          await this.obtenerSaldoUsdt();
        }
      })
    );
  }

  ngOnInit(): void {
    this.isVertical = window.innerWidth <= 768;
    this.checkOperatingHours();
    this.setupTimeMonitoring();
    this.setupRateRefresh();
    this.obtenerTasa();
  }

  ngOnDestroy(): void {
    if (this.timeCheckInterval) {
      clearInterval(this.timeCheckInterval);
    }
    if (this.rateRefreshInterval) {
      clearInterval(this.rateRefreshInterval);
    }
    this.subscription.unsubscribe();
  }

  // --- Step navigation ---

  get totalSteps(): number {
    if (!this.destination || this.destination === 'wallet') return 2;
    return 3; // withdraw: calculator → beneficiario → confirmación
  }

  async openDestinationModal(): Promise<void> {
    if (this.isTransitioning || this.isConverting || this.operationCompleted || this.isModalOpen) return;
    if (!this.isValidAmount) return;

    this.isModalOpen = true;

    // Refrescar tasa antes de continuar
    await this.refrescarTasaYRecalcular();

    const dialogData: UsdtDestinationData = {
      clpAmount: this.montoVes,
      usdtAmount: this.montoUsdt,
      rate: this.tasaVesUsdt,
      sendFormatted: this.formatVES(this.montoVes),
    };

    const dialogRef = this.dialog.open(UsdtDestinationModalComponent, {
      data: dialogData,
      panelClass: 'usdt-destination-dialog',
      maxWidth: '100vw',
      width: this.isVertical ? '100vw' : '520px',
      height: 'auto',
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe(async (result: UsdtDestinationResult | null) => {
      if (!result) {
        this.isModalOpen = false;
        return;
      }

      this.destination = result.destination;

      if (result.destination === 'wallet') {
        this.beneficiarioSeleccionado = null;
        this.goToStep(2);
        this.isModalOpen = false;
      } else {
        // Navegar inmediatamente al paso 2 (muestra spinner de carga)
        this.loadingBeneficiarios = true;
        this.goToStep(2);
        this.isModalOpen = false;
        // Cargar beneficiarios en background — paso 2 ya muestra el loading
        await this.cargarBeneficiarios();
      }
    });
  }

  goToStep(target: 1 | 2 | 3): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.slideDirection = target > this.step ? 'left' : 'right';
    setTimeout(() => {
      this.step = target;
      this.isTransitioning = false;
    }, 300);
  }

  goToStep3(): void {
    if (this.isTransitioning || !this.beneficiarioSeleccionado) return;
    if (this.pausado(this.beneficiarioSeleccionado)) return;
    this.goToStep(3);
  }

  goBack(): void {
    if (this.isTransitioning || this.isConverting) return;

    if (this.step === 1) {
      this.router.navigate(['/wallet/home']);
      return;
    }

    if (this.step === 3) {
      // withdraw: volver a beneficiarios
      this.goToStep(2);
      return;
    }

    // step === 2: volver a calculadora — limpiar DESPUÉS de la transición
    this.goToStep1WithReset();
  }

  goToStep1(): void {
    if (this.isTransitioning || this.isConverting) return;
    this.goToStep1WithReset();
  }

  private goToStep1WithReset(): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.slideDirection = 'right';
    setTimeout(() => {
      this.destination = null;
      this.beneficiarioSeleccionado = null;
      this.step = 1;
      this.isTransitioning = false;
    }, 300);
  }

  navigateBack(): void {
    this.router.navigate(['/wallet/home']);
  }

  // --- Beneficiarios ---

  async cargarBeneficiarios(): Promise<void> {
    if (!this.user?.usuarioId) return;
    this.loadingBeneficiarios = true;
    // En paralelo y sin poder fallar: `listar()` nunca rechaza (si Nest no
    // responde, TRC20 cuenta como activa y decide Nest al retirar).
    const redes = this.mediosCripto.listar();
    try {
      const todos = (await this.recipientCriptoService.getByUser(this.user.usuarioId)) || [];
      this.listaBeneficiarios = todos.filter((b: any) => esRedBinance(redDeBeneficiario(b)));
      this.beneficiariosOtrasRedes = todos.length - this.listaBeneficiarios.length;
      this.listaBeneficiariosFiltrados = this.listaBeneficiarios;
    } catch (error) {
      this.listaBeneficiarios = [];
      this.listaBeneficiariosFiltrados = [];
      this.beneficiariosOtrasRedes = 0;
    } finally {
      this.estadoRedes = await redes;
      this.loadingBeneficiarios = false;
    }
    if (this.beneficiarioSeleccionado && this.pausado(this.beneficiarioSeleccionado)) {
      this.beneficiarioSeleccionado = null;
    }
  }

  /** TRC20 encendida: sin ella no hay retiro posible desde esta pantalla. */
  get redRetiroActiva(): boolean {
    return redActiva(MEDIO_CRIPTO_TRC20, this.estadoRedes);
  }

  /** Franja si TRC20 está pausada. Las demás redes no importan acá. */
  get avisoRedes(): string | null {
    return avisoRedesPausadas(this.estadoRedes, [MEDIO_CRIPTO_TRC20]);
  }

  /** Un beneficiario de una red pausada se ve en la lista, pero no se puede elegir. */
  pausado(b: any): boolean {
    return beneficiarioPausado(b, this.estadoRedes);
  }

  etiquetaRed(b: any): string {
    return etiquetaCortaRed(redDeBeneficiario(b), b?.medioCriptomoneda_descripcion);
  }

  buscarBeneficiario(): void {
    const term = this.searchBeneficiario.toLowerCase().trim();
    if (!term) {
      this.listaBeneficiariosFiltrados = this.listaBeneficiarios;
      return;
    }
    this.listaBeneficiariosFiltrados = this.listaBeneficiarios.filter((b: any) => {
      const nombre = `${b.beneficiario_nombre} ${b.beneficiario_apellido}`.toLowerCase();
      return nombre.includes(term) || (b.beneficiario_medio_recepcion || '').toLowerCase().includes(term);
    });
  }

  elegirBeneficiario(b: any): void {
    if (this.pausado(b)) return;
    if (this.beneficiarioSeleccionado?.beneficiario_id === b.beneficiario_id) {
      this.beneficiarioSeleccionado = null;
    } else {
      this.beneficiarioSeleccionado = b;
    }
  }

  abrirAgregarBeneficiario(): void {
    const dialogRef = this.dialog.open(AddBeneficiaryComponent, {
      // Solo TRC20: un beneficiario de otra red registrado desde acá no
      // aparecería en la lista al volver.
      data: { type: 'CREATE', userID: this.user.usuarioId, soloRedBinance: true },
      maxWidth: '100vw',
      width: this.isVertical ? '100vw' : '520px',
      height: 'auto',
    });
    dialogRef.afterClosed().subscribe(async (result: boolean) => {
      if (result) {
        await this.cargarBeneficiarios();
      } else {
        // El diálogo pudo enterarse de que TRC20 se pausó (422).
        this.estadoRedes = await this.mediosCripto.listar();
      }
    });
  }

  truncateAddress(address: string): string {
    if (!address || address.length <= 12) return address || '';
    return address.substring(0, 6) + '...' + address.substring(address.length - 6);
  }

  // --- Tasa ---

  async obtenerTasa(): Promise<void> {
    this.loadingRate = true;
    this.rateError = null;

    // Tramos en paralelo: si fallan queda la base, nunca frenan la tasa.
    const tramos = this.obtenerTramos();
    try {
      const response = await firstValueFrom(this.mainRatesService.getVesUsdtRateForUser());
      this.estadoTramos = registrarLectura(this.estadoTramos, await tramos, Date.now());

      if (response.success && response.data) {
        this.tasaBase = response.data.tasaFinal;
        this.promedioBinance = response.data.promedioBinance;
        this.comisionPct = response.data.comisionPct;
        this.aplicarTasa(tasaParaMontoVes(this.tramosData, this.montoVes, this.tasaBase));
      } else {
        this.rateError = response.message || 'No se pudo obtener la tasa';
      }
    } catch (error: any) {
      this.estadoTramos = registrarLectura(this.estadoTramos, await tramos, Date.now());
      this.rateError = error?.message || 'Error al obtener la tasa de cambio';
    } finally {
      this.loadingRate = false;
    }
  }

  /**
   * Tramos VES→USDT. Distingue "falló" (`ok: false`, se conservan los de
   * antes) de "llegó vacío" (`ok: true`, todos pagan la base). Nunca lanza.
   */
  private async obtenerTramos(): Promise<LecturaTramos> {
    try {
      const data = await firstValueFrom(this.walletVesClientService.getTramosVesUsdt());
      return { ok: true, data: data ?? null };
    } catch (error) {
      return { ok: false };
    }
  }

  /** Tramos con los que se cotiza ahora: null (la base) si no hay o están desactualizados. */
  get tramosData(): TramosVesUsdtResponse | null {
    return tramosParaCotizar(this.estadoTramos, Date.now());
  }

  /** No se pudieron actualizar los tramos: se muestra la base como "tasa estimada". */
  get tramosSinActualizar(): boolean {
    return tramosDesactualizados(this.estadoTramos, Date.now());
  }

  /** Deja en pantalla la tasa (y el tramo) calculados para el monto. */
  private aplicarTasa(r: TasaTramo): void {
    this.tasaVesUsdt = r.tasa;
    this.tramoActual = r.tramo;
    this.letraTramoActual = r.letra;
    this.ahorroVes = r.ahorroVes;
    this.ahorroUsdt = r.ahorroUsdt;
  }

  /**
   * Vuelve a pedir tasa y tramos y recalcula la tasa efectiva para el MISMO
   * monto VES. Devuelve true (hay que avisar) solo si las dos llamadas
   * respondieron bien y la tasa EMPEORÓ para el cliente más que la tolerancia;
   * si mejoró, se actualiza en silencio. Si los tramos fallan se conservan los
   * de antes y no se avisa nada: el backend acepta la tasa mostrada (±1 %
   * contra la base o el tramo). Si la tasa no se pudo leer, false: se sigue
   * con la que hay, como antes.
   */
  private async refrescarTasaEfectiva(): Promise<boolean> {
    const tramos = this.obtenerTramos();
    try {
      const response = await firstValueFrom(this.mainRatesService.getVesUsdtRateForUser());
      const lectura = await tramos;
      this.estadoTramos = registrarLectura(this.estadoTramos, lectura, Date.now());
      if (!response.success || !response.data) return false;

      const anterior = this.tasaVesUsdt;
      this.tasaBase = response.data.tasaFinal;
      this.promedioBinance = response.data.promedioBinance;
      this.comisionPct = response.data.comisionPct;
      this.aplicarTasa(tasaParaMontoVes(this.tramosData, this.montoVes, this.tasaBase));

      if (this.tasaVesUsdt !== anterior) {
        this.montoUsdt = this.tasaVesUsdt > 0
          ? Math.floor((this.montoVes / this.tasaVesUsdt) * 100) / 100
          : 0;
      }
      if (avisarCambioDeTasa(anterior, this.tasaVesUsdt, lectura.ok)) {
        this.previousRate = anterior;
        return true;
      }
      return false;
    } catch (error) {
      // Sin tasa no se compara nada, pero el fallo (o el acierto) de tramos cuenta.
      this.estadoTramos = registrarLectura(this.estadoTramos, await tramos, Date.now());
      return false;
    }
  }

  private async refrescarTasaYRecalcular(): Promise<void> {
    if (await this.refrescarTasaEfectiva()) {
      this.mostrarWarningTasaCambiada();
    }
  }

  private mostrarWarningTasaCambiada(): void {
    this.showRateChangedWarning = true;
    setTimeout(() => {
      this.showRateChangedWarning = false;
    }, 3000);
  }

  private setupRateRefresh(): void {
    this.rateRefreshInterval = setInterval(() => {
      this.refrescarTasaYRecalcular();
    }, 30000);
  }

  // --- Saldos ---

  async obtenerSaldoVes(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.walletVesClientService.getMyBalance()
      );
      if (response.success && response.data) {
        this.saldoVes = response.data.balanceVes || 0;
      }
    } catch (error) {
      this.saldoVes = 0;
    }
  }

  async obtenerSaldoUsdt(): Promise<void> {
    try {
      const balanceObservable =
        await this.binanceWithdrawalService.getBalance();
      const balance: BalanceResponse = await firstValueFrom(
        balanceObservable
      );
      this.saldoUsdt = balance.availableBalance || 0;
    } catch (error) {
      this.saldoUsdt = 0;
    }
  }

  // --- Horario ---

  private checkOperatingHours(): void {
    const now = new Date();
    const venezuelaTime = new Date(
      now.toLocaleString('en-US', { timeZone: 'America/Caracas' })
    );
    const currentHour = venezuelaTime.getHours();

    this.isOperatingHours = currentHour >= 4 && currentHour < 23;

    if (!this.isOperatingHours) {
      this.resetValues();
      this.errorMessage = this.operatingHoursMessage;
    } else if (this.errorMessage === this.operatingHoursMessage) {
      this.errorMessage = null;
    }
  }

  private setupTimeMonitoring(): void {
    this.timeCheckInterval = setInterval(() => {
      this.checkOperatingHours();
    }, 60000);
  }

  // --- Input/Cálculo ---

  calcularDesdeVes(): void {
    if (!this.isOperatingHours) {
      this.montoUsdt = 0;
      this.errorMessage = this.operatingHoursMessage;
      return;
    }

    // El tramo se re-elige con cada monto, sin aviso: no es un cambio de tasa.
    if (this.tasaBase > 0) {
      this.aplicarTasa(tasaParaMontoVes(this.tramosData, this.montoVes, this.tasaBase));
    }

    if (!this.montoVes || !this.tasaVesUsdt) {
      this.montoUsdt = 0;
      this.errorMessage = null;
      return;
    }

    // VES / tasa = USDT
    this.montoUsdt = Math.floor((this.montoVes / this.tasaVesUsdt) * 100) / 100;
    this.validarMontos();
  }

  calcularDesdeUsdt(): void {
    if (!this.isOperatingHours) {
      this.montoVes = 0;
      this.errorMessage = this.operatingHoursMessage;
      return;
    }

    if (!this.montoUsdt || !this.tasaVesUsdt) {
      this.montoVes = 0;
      if (this.tasaBase > 0) {
        this.aplicarTasa(tasaParaMontoVes(this.tramosData, 0, this.tasaBase));
      }
      this.errorMessage = null;
      return;
    }

    // USDT * tasa = VES, con la tasa del tramo de esos USDT
    if (this.tasaBase > 0) {
      const r = tasaParaMontoUsdt(this.tramosData, this.montoUsdt, this.tasaBase);
      this.aplicarTasa(r);
      this.montoVes = r.montoVes;
    } else {
      this.montoVes = Math.round(this.montoUsdt * this.tasaVesUsdt * 100) / 100;
    }
    this.validarMontos();
  }

  private validarMontos(): void {
    if (this.montoVes > this.saldoVes) {
      this.errorMessage = 'El monto excede tu saldo VES disponible';
    } else if (this.montoUsdt < this.MIN_USDT_AMOUNT) {
      this.errorMessage = `El monto mínimo a recibir es ${this.MIN_USDT_AMOUNT} USDT`;
    } else {
      this.errorMessage = null;
    }
  }

  usarMaximo(): void {
    if (!this.isOperatingHours) return;
    this.montoVes = this.saldoVes;
    this.calcularDesdeVes();
  }

  get isValidAmount(): boolean {
    return (
      this.isOperatingHours &&
      this.tasaVesUsdt > 0 &&
      !this.loadingRate &&
      this.montoVes > 0 &&
      this.montoVes <= this.saldoVes &&
      this.montoUsdt >= this.MIN_USDT_AMOUNT
    );
  }

  get tienesSaldoVes(): boolean {
    return this.saldoVes > 0;
  }

  /**
   * Sin saldo VES, el aviso le sugiere recargar bolívares solo al VE.
   * 30/10/2026: el chileno no recarga bolívares; no se le sugiere.
   */
  get ofreceRecargaVes(): boolean {
    return puedeRecargarVes(this.tipoId);
  }

  // --- Confirmar compra ---

  async confirmarCompra(): Promise<void> {
    // Triple guard: validación + estado de conversión + operación ya completada
    if (!this.isValidAmount || this.isConverting || this.operationCompleted) return;

    // Validar que tenga saldo VES (anti-manipulación de DOM)
    if (!tieneSaldoVes(this.tipoId, this.user?.numeroId)) {
      this.router.navigate(['/wallet/home']);
      return;
    }

    // Bloquear inmediatamente
    this.isConverting = true;
    this.showRateChangedWarning = false;

    // Refrescar tasa antes de enviar (tasa del tramo para el mismo monto)
    if (await this.refrescarTasaEfectiva()) {
      this.showRateChangedWarning = true;
      this.isConverting = false;
      this.goToStep1();
      return;
    }

    const spinnerRef = this.spinner.start();

    const dto = {
      paisOrigen: 'VE',
      userId: this.user.usuarioId,
      totalMonedaOrigen: this.montoVes,
      totalMonedaDestino: this.montoUsdt,
      monedaOrigen: 'VES',
      monedaDestino: 'USDT',
      paisDestino: 'CRIPTOMONEDA',
      formaPago: 'WALLET',
      diferencia: null,
      tasa: this.tasaVesUsdt,
    };

    try {
      await firstValueFrom(this.walletVesClientService.compraUsdt(dto));

      // Marcar como completada ANTES de soltar el spinner
      this.operationCompleted = true;
      this.spinner.stop(spinnerRef);
      this.isConverting = false;

      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'success',
        title: 'Compra realizada',
        text: 'Tu compra de USDT ha sido procesada exitosamente.',
        confirmButtonText: 'Entendido',
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      this.router.navigate(['/wallet/home']);
    } catch (error: any) {
      this.spinner.stop(spinnerRef);
      this.isConverting = false;

      if (esEnvioIncierto(error)) {
        await this.cerrarPorResultadoIncierto();
        return;
      }

      const status = error?.status;
      const message = error?.message || 'Ocurrió un error al procesar la compra.';
      const detalles = error?.detalles;

      // Tasa cambió en backend: refrescar y volver al paso 1
      if (status === 400 && message.includes('tasa')) {
        const tasaVigente = detalles?.tasaVigente;
        if (tasaVigente) {
          this.previousRate = this.tasaVesUsdt;
          this.tasaVesUsdt = tasaVigente;
          this.montoUsdt = Math.floor((this.montoVes / this.tasaVesUsdt) * 100) / 100;
        }
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'warning',
          title: 'Tasa actualizada',
          text: 'La tasa de cambio ha variado. Revisa los nuevos montos y confirma nuevamente.',
          confirmButtonText: 'Entendido',
        });
        this.goToStep1();
        return;
      }

      // Saldo insuficiente: refrescar saldo y volver al paso 1
      if (status === 400 && message.includes('insuficiente')) {
        await this.obtenerSaldoVes();
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'warning',
          title: 'Saldo insuficiente',
          text: detalles
            ? `Tu saldo VES disponible es Bs. ${this.formatVESNumber(detalles.saldoDisponible)}. Ajusta el monto e intenta de nuevo.`
            : message,
          confirmButtonText: 'Entendido',
        });
        this.goToStep1();
        return;
      }

      // Cálculo no cuadra: recalcular y volver al paso 1
      if (status === 400 && message.includes('cálculo')) {
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'warning',
          title: 'Error de cálculo',
          text: 'Los montos no coinciden con la tasa vigente. Se recalculará automáticamente.',
          confirmButtonText: 'Entendido',
        });
        await this.obtenerTasa();
        this.calcularDesdeVes();
        this.goToStep1();
        return;
      }

      // Usuario no autorizado o no VE
      if (status === 403 || (status === 400 && message.includes('venezolanos'))) {
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'error',
          title: 'No autorizado',
          text: message,
          confirmButtonText: 'Entendido',
        });
        this.router.navigate(['/wallet/home']);
        return;
      }

      // Error genérico
      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'error',
        title: 'Error',
        text: message,
        confirmButtonText: 'Entendido',
      });
    }
  }

  /**
   * Resultado incierto (red, 5xx, error aplanado sin mensaje del API): la
   * operación queda cerrada para esta pantalla —`operationCompleted` corta
   * `confirmarCompra` y `confirmarCompraYRetiro`— y el único camino es el
   * historial, donde el cliente VE ve sus movimientos VES y USDT.
   */
  private async cerrarPorResultadoIncierto(): Promise<void> {
    this.operationCompleted = true;
    this.envioIncierto = true;
    await Swal.fire({
      confirmButtonColor: '#1e5a3f',
      icon: 'warning',
      title: 'No pudimos confirmar tu operación',
      text: ConvertVesUsdtComponent.MENSAJE_INCIERTO,
      confirmButtonText: 'Revisar mi historial',
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    this.irAlHistorial();
  }

  /**
   * Historial del cliente: movimientos VES y USDT para el usuario VE. El
   * chileno entra directo a su pestaña VES (por defecto vería la CLP).
   */
  irAlHistorial(): void {
    if (this.tipoId !== 'VE' && tieneSaldoVes(this.tipoId, this.user?.numeroId)) {
      this.router.navigate(['/wallet/client/orders'], { queryParams: { moneda: 'VES' } });
      return;
    }
    this.router.navigate(['/wallet/client/orders']);
  }

  // --- Confirmar compra + retiro (withdraw path) ---

  async confirmarCompraYRetiro(): Promise<void> {
    if (!this.isValidAmount || this.isConverting || this.operationCompleted) return;
    if (!this.beneficiarioSeleccionado) return;
    // Mismo candado que el botón: con TRC20 pausada, o un beneficiario que no
    // es TRC20, Nest rechazaría el retiro.
    if (!this.redRetiroActiva || this.pausado(this.beneficiarioSeleccionado)) return;
    if (!esRedBinance(redDeBeneficiario(this.beneficiarioSeleccionado))) return;
    if (!tieneSaldoVes(this.tipoId, this.user?.numeroId)) {
      this.router.navigate(['/wallet/home']);
      return;
    }

    this.isConverting = true;
    this.showRateChangedWarning = false;

    // Refrescar tasa antes de enviar (tasa del tramo para el mismo monto)
    if (await this.refrescarTasaEfectiva()) {
      this.showRateChangedWarning = true;
      this.isConverting = false;
      this.goToStep1();
      return;
    }

    const spinnerRef = this.spinner.start();

    const dto = {
      walletAddress: this.beneficiarioSeleccionado.beneficiario_medio_recepcion,
      // Siempre TRC20: es la única red que paga Binance y la única que ofrece
      // la lista. Antes se deducía de la descripción de la red y cualquier
      // texto desconocido caía a TRC20 igual: con Polygon o Solana eso habría
      // mandado una dirección de otra red por Tron.
      network: 'TRC20' as const,
      totalMonedaOrigen: this.montoVes,
      tasa: this.tasaVesUsdt,
      fee: 0,
    };

    try {
      const result = await firstValueFrom(this.walletVesClientService.retiroUsdtVes(dto));

      this.operationCompleted = true;
      this.spinner.stop(spinnerRef);
      this.isConverting = false;

      const requiresApproval = result?.requiresApproval;
      const withdrawalId = result?.withdrawalId;

      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'success',
        title: requiresApproval ? 'Retiro en revisión' : 'Compra y retiro procesados',
        text: requiresApproval
          ? `Tu compra de USDT ha sido procesada. El retiro a ${this.truncateAddress(this.beneficiarioSeleccionado.beneficiario_medio_recepcion)} requiere aprobación y será revisado por un operador.`
          : `Tu compra de USDT ha sido procesada. El retiro a ${this.truncateAddress(this.beneficiarioSeleccionado.beneficiario_medio_recepcion)} será enviado en breve.`,
        confirmButtonText: 'Entendido',
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      this.router.navigate(['/wallet/home']);
    } catch (error: any) {
      this.spinner.stop(spinnerRef);
      this.isConverting = false;

      if (esEnvioIncierto(error)) {
        await this.cerrarPorResultadoIncierto();
        return;
      }

      if (esErrorRedInactiva(error)) {
        // 422 `RED_CRIPTO_INACTIVA`: TRC20 se pausó con la pantalla abierta.
        // No salió nada. Vuelve a la lista, que ya la muestra pausada con su
        // franja; la próxima lectura va a Nest en vez de a la caché.
        this.mediosCripto.invalidar();
        this.estadoRedes = conRedPausada(this.estadoRedes, MEDIO_CRIPTO_TRC20);
        this.beneficiarioSeleccionado = null;
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'warning',
          title: 'Red pausada',
          text: mensajeRedInactiva(error),
          confirmButtonText: 'Entendido',
        });
        this.goToStep(2);
        return;
      }

      const status = error?.status;
      const message = error?.message || 'Ocurrió un error al procesar el retiro.';
      const detalles = error?.detalles;

      if (status === 400 && message.includes('tasa')) {
        const tasaVigente = detalles?.tasaVigente;
        if (tasaVigente) {
          this.previousRate = this.tasaVesUsdt;
          this.tasaVesUsdt = tasaVigente;
          this.montoUsdt = Math.floor((this.montoVes / this.tasaVesUsdt) * 100) / 100;
        }
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'warning',
          title: 'Tasa actualizada',
          text: 'La tasa de cambio ha variado. Revisa los nuevos montos y confirma nuevamente.',
          confirmButtonText: 'Entendido',
        });
        this.goToStep1();
        return;
      }

      if (status === 400 && message.includes('insuficiente')) {
        await this.obtenerSaldoVes();
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'warning',
          title: 'Saldo insuficiente',
          text: detalles
            ? `Tu saldo VES disponible es Bs. ${this.formatVESNumber(detalles.saldoDisponible)}. Ajusta el monto e intenta de nuevo.`
            : message,
          confirmButtonText: 'Entendido',
        });
        this.goToStep1();
        return;
      }

      if (status === 400 && message.includes('wallet')) {
        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'error',
          title: 'Dirección inválida',
          text: message,
          confirmButtonText: 'Entendido',
        });
        return;
      }

      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'error',
        title: 'Error',
        text: message,
        confirmButtonText: 'Entendido',
      });
    }
  }

  // --- Escalera de tramos (presentación; la tasa y el envío no cambian) ---

  private escaleraCache: { data: TramosVesUsdtResponse | null; esc: EscalonVista[] | null } | null = null;

  /**
   * Los tramos con los que se cotiza, en la forma de `app-escalera-tramos`.
   * Null (no se dibuja y la pantalla queda como antes) sin tramos, con uno
   * solo, si todos cobran lo mismo o si están desactualizados. Se recalcula
   * solo cuando cambian los datos, no en cada ciclo.
   */
  get escaleraVes(): EscalonVista[] | null {
    const data = this.tramosData;
    if (!this.escaleraCache || this.escaleraCache.data !== data) {
      const esc = data
        ? escaleraDesde(
            tramosValidos(data).map((t) => ({ desde: Number(t.desde), tasa: Number(t.tasa) })),
            { unidadCorte: 'USDT', monedaMonto: 'VES', tipoAhorro: 'PAGA_MENOS', exigirMejora: true },
          )
        : null;
      this.escaleraCache = { data, esc };
    }
    return this.escaleraCache.esc;
  }

  /** Base con la que se elige el tramo (`montoVes ÷ base`), la misma de `tasaParaMontoVes`. */
  get baseEscaleraVes(): number {
    const b = Number(this.tramosData?.base);
    return Number.isFinite(b) && b > 0 ? b : this.tasaBase;
  }

  /** «Usar X» de la escalera: escribe los bolívares en el campo, como si los tecleara. */
  usarMontoVes(monto: number): void {
    if (!this.isOperatingHours || !(monto > 0)) return;
    this.montoVes = monto;
    this.calcularDesdeVes();
  }

  /** Porcentaje con coma decimal y espacio antes del signo: «1,25 %». */
  formatPct(value: number | null | undefined): string {
    const n = Number(value);
    if (value == null || !Number.isFinite(n)) return '—';
    return `${n.toLocaleString('es-CL', { maximumFractionDigits: 2 })} %`;
  }

  // --- Helpers ---

  resetValues(): void {
    this.montoVes = 0;
    this.montoUsdt = 0;
    this.errorMessage = null;
  }

  formatVES(value: number): string {
    if (!value && value !== 0) return 'Bs. 0,00';
    const roundedValue = Math.round(value * 100) / 100;
    return `Bs. ${roundedValue.toLocaleString('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  formatVESNumber(value: number): string {
    if (!value && value !== 0) return '0,00';
    const roundedValue = Math.round(value * 100) / 100;
    return roundedValue.toLocaleString('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatUSDT(value: number): string {
    if (!value && value !== 0) return '0.00';
    const roundedValue = Math.round(value * 100) / 100;
    return roundedValue.toFixed(2);
  }

  formatUSDTWithSymbol(value: number): string {
    if (!value && value !== 0) return '$0.00 USDT';
    const roundedValue = Math.round(value * 100) / 100;
    return `$${roundedValue.toFixed(2)} USDT`;
  }
}
